import React, {useState, useEffect} from 'react'
import {Button} from 'reactstrap'
import {useTranslation} from 'react-i18next'
import compose from 'lodash/flowRight'
import useSmartNavigate from 'hooks/useSmartNavigate'

import {breadcrumbsStore} from 'appBase/TopNav'

import withUriParams from 'hocs/withUriParams'
import withInitialDataLoadWaiting from 'hocs/withInitialDataLoadWaiting'

import CheckBox from 'components/elements/CheckBox'
import PanelModal from 'components/modals/PanelModal'
import NamedBlock from 'components/blocks/NamedBlock'
import ListWithSort from 'components/blocks/ListWithSort'

import entityUtils from 'utils/entityUtils'

import {queryLogs} from '../data/serviceQueries'
import {SERVICE_LOGS_VIEW} from 'constants/constants'


const LogsView = compose(
     withUriParams([['expression', String, {optional: true}]]),
     queryLogs,
     withInitialDataLoadWaiting(['logs'])
)((props) => {
    const {expression, logs, uri} = props;
    const {t} = useTranslation();
    const [currentLog, setCurrentLog] = useState(null);
    const navigate = useSmartNavigate();

    // breadcrumbs
    breadcrumbsStore.register(SERVICE_LOGS_VIEW);
    useEffect(() => {
        breadcrumbsStore.set(SERVICE_LOGS_VIEW, {name: t('appService.logs'), to: `${props.uri}`}, true);
    });

    // registered objectTypes
    const registeredObjectTypes = [
        {field: "catalog", value: t('appService.views.LogsView.objectTypes.catalog')},
        {field: "catalog_entry", value: t('appService.views.LogsView.objectTypes.catalogEntry')},
        {field: "catalog_entry_version", value: t('appService.views.LogsView.objectTypes.catalogEntryVersion')},
        {field: "device", value: t('appService.views.LogsView.objectTypes.device')},
        {field: "event", value: t('appService.views.LogsView.objectTypes.event')},
        {field: "license", value: t('appService.views.LogsView.objectTypes.license')},
        {field: "region", value: t('appService.views.LogsView.objectTypes.region')},
        {field: "scenario", value: t('appService.views.LogsView.objectTypes.scenario')},
        {field: "solution", value: t('appService.views.LogsView.objectTypes.solution')},
        {field: "user", value: t('appService.views.LogsView.objectTypes.user')}
    ];

    // parsing expression, extracting object types
    let objectTypes = [];
    const objectTypesPart = expression?.match(/\[(.+)\]/);
    if (objectTypesPart) {
        objectTypes = objectTypesPart[1].split(",").filter(e => e);
    }

    // ...extracting author ids
    let authorIds = [];
    const authorIdsPart = expression?.match(/\((.+)\)/);
    if (authorIdsPart) {
        authorIds = authorIdsPart[1].split(",").filter(e => e);
    }

    // ...extracting object ids
    let objectIds = [];
    const objectIdsPart = expression?.match(/«(.+)»/);
    if (objectIdsPart) {
        objectIds = objectIdsPart[1].split(",").filter(e => e);
    }

    // ...extracting page
    let page = 0;
    const pagePart = expression?.match(/<(.+)>/);
    if (pagePart) {
        const safeNumber = (val) => {
            const number = Number(val);
            return Number.isNaN(number) ? 0: number;
        };
        page = safeNumber(pagePart[1]);
    }

    const onChange = (type, e, value) => {
        let newExpression;
        switch(type) {
            case "objectType":
                // preserving authorIds, dropping object ids and page
                const checked = e.nativeEvent.target.checked;

                let newObjectTypes;
                if (checked) {
                   newObjectTypes = [...objectTypes, value].join(',');
                } else {
                   newObjectTypes = objectTypes.filter(v => v !== value).join(',');
                }
                newExpression = '[' + newObjectTypes + ']';
                newExpression += '(' + authorIds.join(',') + ')';
                break;
            case "authorId":
                // preserving objectTypes and objectIds, dropping page
                let newAuthorIds;
                newExpression = "[" + objectTypes.join(',') + "]";
                if (e) {
                    newAuthorIds = [...authorIds.filter(id => id !== String(value)), value];
                    setCurrentLog(null);
                } else {
                    newAuthorIds = [authorIds.filter(id => id !== String(value))];
                }
                newExpression += '(' + newAuthorIds + ')';
                newExpression += "«" + objectIds.join(',') + "»";
                break;
            case "objectId":
                // preserving fixed objectType, preserving authorIds, dropping page
                let newObjectIds;
                if (e) {
                    newExpression = "[" + value.objectType + "]";
                    newObjectIds = [...objectIds.filter(id => id !== String(value.id)), value.id];
                    setCurrentLog(null);
                } else {
                    newExpression = "[" + objectTypes.join(',') + "]";
                    newObjectIds = [objectIds.filter(id => id !== String(value.id))];
                }
                newExpression += '(' + authorIds + ')';
                newExpression += "«" + newObjectIds + "»";
                break;
            case "page":
                // preserving all other values
                newExpression = "[" + objectTypes.join(',') + "]";
                newExpression += "(" + authorIds.join(',') + ")";
                newExpression += "«" + objectIds.join(',') + "»";
                page = page + value;
                newExpression += '<' + page + '>';
                break;
            default:
                throw new Error(`Unknown type ${type}`);
        }
        navigate(`${uri}/${newExpression}`);
    };

    // checkBoxes for object types
    const objectTypesCheckBoxes = registeredObjectTypes.map((t, i) =>
        (<CheckBox checked={objectTypes.includes(t.field)} key={i} label={t.value}
            onChange={(e) => onChange("objectType", e, t.field)} />));

    // checkBoxes for authors ids
    const authorsCheckBoxes = authorIds.map((id, i) =>
        (<CheckBox checked key={i} label={id}
            onChange={(e) => onChange('authorId', false, id)} />));

    // checkBoxes for objects ids
    const objectsCheckBoxes = objectIds.map((id, i) =>
        (<CheckBox checked key={i} label={id}
            onChange={(e) => onChange('objectId', false, {id})} />));

    // rows
    const rows = logs.map(log => {
        const parameters = entityUtils.safeJSONParse(log.parameters);
        return {
            fields: [
                log.id,
                log.objectType,
                log.objectId,
                log.message,
                log.authorId,
                parameters.author_email,
                parameters.author_name,
                log.createdAt && log.createdAt.substring(0, 19).replace('T', ' '),
            ],
            actions: {
                "select": () => setCurrentLog({
                    id: log.objectId,
                    authorId: log.authorId,
                    objectType: log.objectType,
                    message: log.message,
                    parameters
                })
            },
            className: (log.level === 2) ?
                            "text-success":
                       (log.level === 3) ?
                            "text-danger":
                            "",
        }
    });

    const currentLogKeys = currentLog ? Object.keys(currentLog.parameters): [];
    const currentLogRows = currentLogKeys.map(key => ({
        fields: [key, currentLog.parameters[key]]
    }));
    currentLogRows.unshift({fields: [t('appService.views.LogsView.authorId'), currentLog?.authorId]});
    currentLogRows.unshift({fields: [t('appService.views.LogsView.objectId'), currentLog?.id]});

    return (
        <div className="d-flex flex-column h-100 w-100">
            <div className="flex-grow d-flex flex-column scroll-parent">
                <div className="flex-grow d-flex scroll-parent">
                    <div className="flex-grow scroll">

                        <div className="container mt-4 ms-1">
                            <div className="row">
                                <div className="col-3">
                                    <NamedBlock name={t('appService.views.LogsView.objectTypeFilters')}>
                                        {objectTypesCheckBoxes}
                                    </NamedBlock>
                                </div>

                                {authorIds && !!authorIds.length &&
                                <div className="col-3">
                                    <NamedBlock name={t('appService.views.LogsView.authorFilters')}>
                                        {authorsCheckBoxes}
                                    </NamedBlock>
                                </div>}

                                {objectIds && !!objectIds.length &&
                                <div className="col-3">
                                    <NamedBlock name={t('appService.views.LogsView.objectFilters')}>
                                        {objectsCheckBoxes}
                                    </NamedBlock>
                                </div>}
                            </div>
                        </div>

                        <h5 className="text-center mt-3">{t('appService.logs')}</h5>

                        <div className="container-fluid">
                            <div className="d-flex justify-content-between mb-3">
                                <Button disabled={!page} color={page ? "primary": "secondary"} className="m-2"
                                        onClick={() => onChange("page", true, -1)}>
                                    {t('appService.views.LogsView.newer')}
                                </Button>

                                <div className={'ms-auto'}>
                                    <Button color="primary" className="m-2"
                                            onClick={() => onChange("page", true, 1)}>
                                        {t('appService.views.LogsView.older')}
                                    </Button>
                                </div>
                            </div>

                            <NamedBlock>
                                <ListWithSort headers={[t('appService.views.LogsView.id'),
                                                        t('appService.views.LogsView.objectType'),
                                                        t('appService.views.LogsView.objectId'),
                                                        t('appService.views.LogsView.message'),
                                                        t('appService.views.LogsView.authorId'),
                                                        t('appService.views.LogsView.email'),
                                                        t('appService.views.LogsView.name'),
                                                        t('appService.views.LogsView.createdAt')]}
                                              sizes={["40*", "100*", "40*", "200*", "40*", "190*", "120*",  "100*"]}
                                              rows={rows} />
                            </NamedBlock>
                        </div>

                        {currentLog &&
                        <PanelModal show title={currentLog.message} noPadding scrollable onClose={() => setCurrentLog(null)}>
                           <div>
                               <ListWithSort rows={currentLogRows} sizes={["30*", "70*"]} />
                               <Button color="primary" className="m-2"
                                       onClick={() => onChange("objectId", true, {id: currentLog.id, objectType: currentLog.objectType})}>
                                   {t('appService.views.LogsView.allObjectActions')}
                               </Button>
                               <Button color="primary" className="m-2"
                                       onClick={() => onChange("authorId", true, currentLog.authorId)}>
                                   {t('appService.views.LogsView.allAuthorActions')}
                                </Button>
                            </div>
                        </PanelModal>}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default LogsView;